import { t } from '@/lib/i18n'

/**
 * 稿子的 `.phone` / `.screen`：**把打卡界面画出来**。
 *
 * 为什么值得单独一个组件：原来这一页放的是一张假看板卡（三个统计数字 + 一条
 * 折线），那张卡说明的是「有数据」；而访客要判断的是「一天是怎么过的」——
 * 四件事、两件划掉了、点一下加分。样机比统计卡更接近那个判断。
 *
 * 整块 `aria-hidden`：屏幕阅读器读它只会得到一串脱离语境的数字，
 * 而它旁边那段正文已经把同一件事说清了。
 *
 * 里面每一块都与 app 里真的组件同构（顶栏 = TopBar、进度环 = ProgressRing、
 * 行 = Row、底部 = TabBar）——刻意的：样机要长得像用户装完之后看到的东西，
 * 不是一张更好看的示意图。
 */
export default function PhoneMock() {
  const tasks = [
    { emoji: '📖', name: t('home.mock.task_read'), pts: 3, done: true },
    { emoji: '🏃', name: t('home.mock.task_run'), pts: 2, done: true },
    { emoji: '🍚', name: t('home.mock.task_dishes'), pts: 2, done: false },
    { emoji: '✏️', name: t('home.mock.task_homework'), pts: 3, done: false },
  ]

  // 进度环用 stroke-dasharray 画，不引图表库（仓库纪律：零新增前端依赖）
  const r = 22
  const circumference = 2 * Math.PI * r

  return (
    <div
      aria-hidden="true"
      className="mx-auto w-full max-w-[300px] rounded-[30px] border-[7px] border-ink/85 bg-card shadow-card overflow-hidden select-none"
    >
      <div className="flex items-center gap-2 px-3.5 py-2.5 border-b border-line">
        <span className="w-7 h-7 rounded-full grid place-items-center bg-gradient-to-br from-brand to-brand2 text-white text-[13px] font-bold">
          {t('home.mock.avatar')}
        </span>
        <span className="font-semibold text-[14px]">{t('home.mock.name')}</span>
        <span className="flex-1" />
        <span className="px-2.5 py-1 rounded-full bg-tint text-brand font-bold text-xs">💰 320</span>
      </div>

      <div className="px-3.5 py-3">
        <div className="flex items-center gap-3 mb-3">
          <span className="relative w-[52px] h-[52px] shrink-0">
            <svg viewBox="0 0 52 52" className="w-full h-full -rotate-90">
              <circle cx="26" cy="26" r={r} fill="none" stroke="var(--color-sunk)" strokeWidth="6" />
              <circle
                cx="26" cy="26" r={r} fill="none" stroke="var(--color-brand)" strokeWidth="6"
                strokeLinecap="round"
                strokeDasharray={`${(4 / 7) * circumference} ${circumference}`}
              />
            </svg>
            <span className="absolute inset-0 grid place-items-center text-[13px] font-extrabold">4/7</span>
          </span>
          <span className="min-w-0">
            <b className="block text-[14px]">{t('home.mock.left')}</b>
            <span className="block text-faint text-[11px] mt-0.5">{t('home.mock.earned')}</span>
          </span>
        </div>

        <div className="grid gap-1.5">
          {tasks.map((task) => (
            <div
              key={task.name}
              className={
                'flex items-center gap-2.5 px-2.5 py-2 rounded-ctl bg-sunk ' +
                (task.done ? 'opacity-55' : '')
              }
            >
              <span className="text-base">{task.emoji}</span>
              <span className={
                'flex-1 min-w-0 text-[13px] font-semibold truncate ' + (task.done ? 'line-through' : '')
              }>
                {task.name}
              </span>
              <span className={
                'px-2 py-0.5 rounded-full text-[11px] font-bold ' +
                (task.done ? 'bg-green-tint text-green' : 'bg-tint text-brand')
              }>
                +{task.pts}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-4 border-t border-line text-[10px] text-faint">
        {[
          { icon: '📊', label: t('layout.tabs.dashboard') },
          { icon: '🎯', label: t('layout.tabs.goals') },
          { icon: '✅', label: t('layout.tabs.checkin'), on: true },
          { icon: '🛍️', label: t('layout.tabs.store') },
        ].map((tab) => (
          <span
            key={tab.label}
            className={'flex flex-col items-center gap-0.5 py-2 ' + (tab.on ? 'text-brand font-bold' : '')}
          >
            <span className="text-[15px]">{tab.icon}</span>
            {tab.label}
          </span>
        ))}
      </div>
    </div>
  )
}
